import { Send, Person, SmartToy } from '@mui/icons-material';
import {
    Box,
    Paper,
    Typography,
    TextField,
    IconButton,
    Avatar,
    Stack,
    Chip,
    CircularProgress,
    Fade,
    Divider,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'redux/store';
import type { ChatMessage } from 'types';

interface ChatInterfaceProps {
    messages: ChatMessage[];
    chatGroupId: number;
    users: string[];
    onSendMessage: (text: string) => void;
    isSending: boolean;
    isLoading: boolean;
    hasAccess?: boolean;
}

const parseDate = (timestamp: string | null | undefined): Date | null => {
    if (!timestamp) return null;

    try {
        // Try parsing as Date string (handles various formats including HTTP date)
        const date = new Date(timestamp);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return null;
        }

        return date;
    } catch (error) {
        console.error('Error parsing date:', error);
        return null;
    }
};

const formatMessageTime = (timestamp: string | null | undefined) => {
    const date = parseDate(timestamp);

    if (!date) {
        return '';
    }

    try {
        return format(date, 'h:mm a');
    } catch (error) {
        console.error('Error formatting message time:', error);
        return '';
    }
};

const formatDateDivider = (timestamp: string | null | undefined) => {
    const date = parseDate(timestamp);

    if (!date) {
        return '';
    }

    try {
        if (isToday(date)) {
            return 'Today';
        } else if (isYesterday(date)) {
            return 'Yesterday';
        } else {
            return format(date, 'MMMM d, yyyy');
        }
    } catch (error) {
        console.error('Error formatting date divider:', error);
        return '';
    }
};

const groupMessagesByDate = (messages: ChatMessage[]) => {
    const groups: { date: string; messages: ChatMessage[] }[] = [];

    messages.forEach((message) => {
        // Skip "Chat created!" system message
        if (message.user_id === null && message.text?.toLowerCase() === 'chat created!') {
            return;
        }

        if (!message.created_at) return;

        const messageDate = parseDate(message.created_at);
        if (!messageDate) return;

        const lastGroup = groups[groups.length - 1];

        if (lastGroup && lastGroup.date) {
            const lastDate = parseDate(lastGroup.messages[0].created_at);
            if (lastDate && isSameDay(lastDate, messageDate)) {
                lastGroup.messages.push(message);
                return;
            }
        }

        groups.push({
            date: message.created_at,
            messages: [message],
        });
    });

    return groups;
};

const ChatInterface = ({
    messages,
    users,
    onSendMessage,
    isSending,
    isLoading,
    hasAccess = true,
}: ChatInterfaceProps) => {
    const user = useSelector((state: RootState) => state.user.entity);

    const [messageText, setMessageText] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = () => {
        if (messageText.trim() && !isSending) {
            onSendMessage(messageText.trim());
            setMessageText('');
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const isOwnMessage = (message: ChatMessage) => {
        return message.user_id === user?.id;
    };

    const isSystemMessage = (message: ChatMessage) => {
        return message.user_id === null;
    };

    const messageGroups = groupMessagesByDate(messages);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '60vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: '100dvh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                bgcolor: 'grey.50',
            }}
        >
            <Box
                sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                {/* Chat Header */}
                <Box
                    sx={{
                        px: 3,
                        py: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Expert Review Chat
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        {users.length} participant{users.length > 1 ? 's' : ''}: {users.join(', ')}
                    </Typography>
                </Box>

                {/* Messages Container */}
                <Box
                    ref={messagesContainerRef}
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        p: 3,
                        bgcolor: 'grey.50',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            bgcolor: 'grey.100',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            bgcolor: 'grey.400',
                            borderRadius: '4px',
                            '&:hover': {
                                bgcolor: 'grey.500',
                            },
                        },
                    }}
                >
                    <Stack spacing={2}>
                        {messageGroups.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '40vh',
                                    gap: 2,
                                }}
                            >
                                <SmartToy
                                    sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }}
                                />
                                <Typography variant="h6" color="text.secondary">
                                    No messages yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Start the conversation by sending a message below
                                </Typography>
                            </Box>
                        ) : (
                            messageGroups.map((group, groupIndex) => (
                                <Box key={groupIndex}>
                                    {/* Date Divider */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            my: 3,
                                        }}
                                    >
                                        <Divider sx={{ flexGrow: 1 }} />
                                        <Chip
                                            label={formatDateDivider(group.date)}
                                            size="small"
                                            sx={{
                                                mx: 2,
                                                bgcolor: 'white',
                                                color: 'text.secondary',
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        />
                                        <Divider sx={{ flexGrow: 1 }} />
                                    </Box>

                                    {/* Messages for this date */}
                                    <Stack spacing={1.5}>
                                        {group.messages.map((message) => {
                                            const isOwn = isOwnMessage(message);
                                            const isSystem = isSystemMessage(message);

                                            if (isSystem) {
                                                return (
                                                    <Fade in key={message.id}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                my: 1,
                                                            }}
                                                        >
                                                            <Chip
                                                                icon={<SmartToy fontSize="small" />}
                                                                label={message.text}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: 'grey.200',
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Fade>
                                                );
                                            }

                                            return (
                                                <Fade in key={message.id}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: isOwn
                                                                ? 'flex-end'
                                                                : 'flex-start',
                                                            gap: 1,
                                                            alignItems: 'flex-start',
                                                        }}
                                                    >
                                                        {!isOwn && (
                                                            <Avatar
                                                                sx={{
                                                                    width: 36,
                                                                    height: 36,
                                                                    bgcolor: 'primary.main',
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 600,
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {message.user?.[0]?.toUpperCase() || (
                                                                    <Person fontSize="small" />
                                                                )}
                                                            </Avatar>
                                                        )}

                                                        <Box
                                                            sx={{
                                                                maxWidth: '65%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 0.5,
                                                                alignItems: isOwn
                                                                    ? 'flex-end'
                                                                    : 'flex-start',
                                                            }}
                                                        >
                                                            {!isOwn && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        px: 1.5,
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {message.user}
                                                                </Typography>
                                                            )}

                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-end',
                                                                    gap: 1,
                                                                    flexDirection: isOwn
                                                                        ? 'row-reverse'
                                                                        : 'row',
                                                                }}
                                                            >
                                                                <Paper
                                                                    elevation={0}
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 1.25,
                                                                        bgcolor: isOwn
                                                                            ? 'primary.main'
                                                                            : 'white',
                                                                        color: isOwn
                                                                            ? 'white'
                                                                            : 'text.primary',
                                                                        borderRadius: 0.5,
                                                                        //   borderTopRightRadius: isOwn ? 0.5 : 2.5,
                                                                        //   borderTopLeftRadius: isOwn ? 2.5 : 0.5,
                                                                        border: isOwn
                                                                            ? 'none'
                                                                            : '1px solid',
                                                                        borderColor: 'divider',
                                                                        boxShadow: isOwn
                                                                            ? '0 2px 8px rgba(0,0,0,0.1)'
                                                                            : 'none',
                                                                        display: 'inline-block',
                                                                        width: 'fit-content',
                                                                        maxWidth: '100%',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            wordBreak: 'break-word',
                                                                            whiteSpace: 'pre-wrap',
                                                                            lineHeight: 1.6,
                                                                            fontSize: '0.9375rem',
                                                                        }}
                                                                    >
                                                                        {message.text}
                                                                    </Typography>
                                                                </Paper>

                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{
                                                                        fontSize: '0.688rem',
                                                                        mb: 0.5,
                                                                        opacity: 0.7,
                                                                        minWidth: 'fit-content',
                                                                    }}
                                                                >
                                                                    {formatMessageTime(
                                                                        message.created_at
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        {isOwn && (
                                                            <Avatar
                                                                sx={{
                                                                    width: 36,
                                                                    height: 36,
                                                                    bgcolor: 'secondary.main',
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 600,
                                                                    mt: 0.5,
                                                                }}
                                                            >
                                                                {user?.firstname?.[0]?.toUpperCase() ||
                                                                    user?.email?.[0]?.toUpperCase() ||
                                                                    'U'}
                                                            </Avatar>
                                                        )}
                                                    </Box>
                                                </Fade>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            ))
                        )}
                        <div ref={messagesEndRef} />
                    </Stack>
                </Box>

                <Divider />

                {/* Input Area */}
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'white',
                        flexShrink: 0,
                    }}
                >
                    {/* Message Input */}
                    <Box sx={{ p: 2.5 }}>
                        <Tooltip
                            title={!hasAccess ? 'You are not a member of this chat' : ''}
                            placement="top"
                        >
                            <TextField
                                fullWidth
                                multiline
                                maxRows={4}
                                placeholder={
                                    hasAccess
                                        ? 'Ask your immigration expert anything...'
                                        : 'You do not have access to this chat'
                                }
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isSending || !hasAccess}
                                autoFocus={hasAccess}
                                slotProps={{
                                    input: {
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleSend}
                                                    disabled={
                                                        !messageText.trim() ||
                                                        isSending ||
                                                        !hasAccess
                                                    }
                                                    sx={{
                                                        bgcolor: 'primary.main',
                                                        color: 'white',
                                                        '&:hover': {
                                                            bgcolor: 'primary.dark',
                                                        },
                                                        '&.Mui-disabled': {
                                                            bgcolor: 'action.disabledBackground',
                                                        },
                                                    }}
                                                >
                                                    {isSending ? (
                                                        <CircularProgress
                                                            size={20}
                                                            color="inherit"
                                                        />
                                                    ) : (
                                                        <Send fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        bgcolor: 'background.default',
                                    },
                                }}
                            />
                        </Tooltip>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ChatInterface;
